import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography, FormControlLabel, FormGroup } from "@mui/material";
import PrimaryCheckbox from "../../../Inputs/PrimaryCheckBox/PrimaryCheckBox";
import PrimaryButton from "../../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../../Buttons/SecondaryButton/SecondaryButton";
import StyledPopover from "../../../StyledPopover/StyledPopover";
import FilterButton from "../../../Buttons/FilterButton/FilterButton";
import LoadStatusPill from "../../../Pills/LoadStatusPill/LoadStatusPill";
import useLoadStatus from "../../../../hooks/useLoadStatus/useLoadStatus";

export default function BrokerageStatusFilter({
  column: { filterValue, setFilter, Header, unSelected, preSelected },
}) {
  const { loadStatuses } = useLoadStatus();
  const [anchorEl, setAnchorEl] = useState(null);
  const [values, setValues] = useState([]);
  const [isArg, setIsArg] = useState(false);
  const [isAva, setIsAva] = useState(false);
  const [isCov, setIsCov] = useState(false);
  const [isDis, setIsDis] = useState(false);
  const [isLoa, setIsLoa] = useState(false);
  const [isLoad, setIsLoad] = useState(false);
  const [isDel, setIsDel] = useState(false);
  const [isDelv, setIsDelv] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const open = Boolean(anchorEl);
  const pid = open ? "brokerage-status-filter-popover" : undefined;

  const includeSelected = (id) => {
    return unSelected ? unSelected.includes(id) : false;
  };

  const statuses = useMemo(() => [
    {
      id: "A-RTGDE",
      value: isArg,
      disabled: includeSelected("A-RTGDE"),
    },
    {
      id: "AVA",
      value: isAva,
      disabled: includeSelected("AVA"),
    },
    {
      id: "COV",
      value: isCov,
      disabled: includeSelected("COV"),
    },
    {
      id: "DIS",
      value: isDis,
      disabled: includeSelected("DIS"),
    },
    {
      id: "LOA",
      value: isLoa,
      disabled: includeSelected("LOA"),
    },
    {
      id: "LOAD",
      value: isLoad,
      disabled: includeSelected("LOAD"),
    },
    {
      id: "DEL",
      value: isDel,
      disabled: includeSelected("DEL"),
    },
    {
      id: "DELV",
      value: isDelv,
      disabled: includeSelected("DELV"),
    },
    {
      id: "ONHOLD",
      value: isOnHold,
      disabled: includeSelected("ONHOLD"),
    }
  ], [
    isArg,
    isAva,
    isCov,
    isDis,
    isLoa,
    isLoad,
    isDel,
    isDelv,
    isOnHold
  ]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelected = (event) => {
    const status = event.currentTarget.value;
    const value = event.target.checked;

    setValues(prevValues => value ? [...prevValues, status] : prevValues.filter(val => val !== status));

    switch (status) {
      case "COV":
        setIsCov(value);
        break;
      case "LOAD":
        setIsLoad(value);
        break;
      case "LOA":
        setIsLoa(value);
        break;
      case "DIS":
        setIsDis(value);
        break;
      case "DEL":
        setIsDel(value);
        break;
      case "DELV":
        setIsDelv(value);
        break;
      case "AVA":
        setIsAva(value);
        break;
      case "A-RTGDE":
        setIsArg(value);
        break;
      case "ONHOLD":
        setIsOnHold(value);
        break;
      default:
        break;
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEnter = () => {
    setFilter(values);
    handleClose();
  };

  const handleClear = () => {
    handleDeselectAll();
    setFilter(undefined);
    handleClose();
  };

  const handleSelectAll = () => {
    setIsCov(!includeSelected("COV"));
    setIsLoad(!includeSelected("LOAD"));
    setIsLoa(!includeSelected("LOA"));
    setIsDis(!includeSelected("DIS"));
    setIsDel(!includeSelected("DEL"));
    setIsDelv(!includeSelected("DELV"));
    setIsAva(!includeSelected("AVA"));
    setIsArg(!includeSelected("A-RTGDE"));
    setIsOnHold(!includeSelected("ONHOLD"));
    setValues(unSelected ? loadStatuses.filter(({ id }) => !unSelected.includes(id)).map(({ id }) => id) : loadStatuses.map(({ id }) => id));
  };
  
  const handleDeselectAll = () => {
    setIsCov(false);
    setIsLoad(false);
    setIsLoa(false);
    setIsDis(false);
    setIsDel(false);
    setIsDelv(false);
    setIsAva(false);
    setIsArg(false);
    setIsOnHold(false);
    setValues([]);
  };

  useEffect(() => {
    if (!filterValue) {
      handleClear();
    }
  }, [filterValue]);

  useEffect(() => {
    if (preSelected) {
      setFilter(preSelected);
      setValues(preSelected);
      preSelected.forEach(filter => {
        switch (filter) {
          case "COV":
            setIsCov(true);
            break;
          case "LOAD":
            setIsLoad(true);
            break;
          case "LOA":
            setIsLoa(true);
            break;
          case "DIS":
            setIsDis(true);
            break;
          case "DEL":
            setIsDel(true);
            break;
          case "DELV":
            setIsDelv(true);
            break;
          case "AVA":
            setIsAva(true);
            break;
          case "A-RTGDE":
            setIsArg(true);
            break;
          case "ONHOLD":
            setIsOnHold(true);
            break;
          default:
            break;
        }
      });
    }
  }, [preSelected]);

  return (
    <>
      <FilterButton
        filterValue={filterValue}
        onClick={handleOpen}
      />
      <StyledPopover
        id={pid}
        open={open}
        anchorEl={anchorEl}
        titleObject={
          <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }} >
            <Typography variant="subhead1">Sort by {Header}</Typography>
          </span>
        }
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-evenly", color: "primary.60", textDecoration: "underline", cursor: "pointer" }}>
          <Typography onClick={handleSelectAll}>Select All</Typography>
          <Typography onClick={handleDeselectAll}>Deselect All</Typography>
        </Box>
        <Box sx={{
          margin: "20px 0 25px 0",
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)"
        }}>
          <FormGroup sx={{ marginTop: "20px", height: "170px", width: "120px" }}>
            {statuses.map((status, i) => (
              <FormControlLabel
                key={`checkbox-${i}`}
                control={
                  <Box sx={{ margin: "8px " }}>
                    <PrimaryCheckbox
                      checked={status.value}
                      handleChange={handleSelected}
                      value={status.id}
                      color="black_solid.60"
                      disabled={status.disabled}
                    />
                  </Box>
                }
                label={
                  <LoadStatusPill value={status.id} darkTheme />
                }
              />
            ))}
          </FormGroup>
        </Box>
        <Box sx={{ display: "flex" }}>
          {filterValue &&
            <SecondaryButton
              label="Clear"
              onClick={handleClear}
            />
          }
          <Box sx={{ margin: "0 20px 0 auto" }}>
            <SecondaryButton
              label="Cancel"
              onClick={handleClose}
            />
          </Box>
          <PrimaryButton
            label="Go"
            onClick={handleEnter}
            width="50px"
          />
        </Box>
      </StyledPopover>
    </>
  );
}

BrokerageStatusFilter.propTypes = {
  column: PropTypes.object.isRequired
};